import { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { fetchUser } from '../../features/auth/authSlice';
import { useDisableProviderChangedMutation } from '../../app/services/users';
import CustomTabs from '../../components/CustomTabs';
import ConfirmReauthorization from './ConfirmReauthorization';
import AccountSecurity from './AccountSecurity';
import AccountTab from './AccountTab';
import UserProfileNotification from './UserProfileNotification';

import { Typography } from '@mui/material';

import styles from './style.module.css';

import Cookies from 'js-cookie';
import removeData from '../../helpers/removeData';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Toast from '../../components/Toast';

function UserProfile() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL query parameter or default to 'account'
    const tabParam = searchParams.get('tab');
    return ['account', 'security', 'notifications'].includes(tabParam)
      ? tabParam
      : 'account';
  });
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [disableProviderChanged] = useDisableProviderChangedMutation();

  const handleCloseToast = async () => {
    await disableProviderChanged()
      .unwrap()
      .then(() => {
        dispatch(fetchUser());
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const navigate = useNavigate();
  const handleLogout = () => {
    Cookies.remove('token');
    removeData(dispatch);
    navigate('/sign-in');
  };

  // Set default tab parameter if none exists
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam) {
      setSearchParams({ tab: 'account' }, { replace: true });
    }
  }, []);

  // Update activeTab when URL parameter changes (browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['account', 'security', 'notifications'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Helper function to change tab and update URL
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey });
  };

  const tabs = [
    {
      key: 'account',
      label: t('generic.profileAccount'),
      onClick: () => handleTabChange('account'),
    },
    {
      key: 'security',
      label: t('generic.profileSecurity'),
      onClick: () => handleTabChange('security'),
    },
    {
      key: 'notifications',
      label: t('generic.profileNotifications'),
      onClick: () => handleTabChange('notifications'),
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return <AccountTab onEmailChange={handleOpen} />;
      case 'security':
        return (
          <div className={styles.account}>
            <AccountSecurity
              userId={user?.id}
              provider={user?.provider}
              providerChanged={user?.provider_changed}
            />
          </div>
        );
      case 'notifications':
        return (
          <div className={styles.account}>
            <UserProfileNotification />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <section className={styles.container}>
        <div className={styles.header}>
          <Typography variant="h5" fontWeight="bold">
            {t('generic.profile')}
          </Typography>
          <Typography>{t('generic.profileSubtitle')}</Typography>
        </div>
        <CustomTabs tabs={tabs} activeTab={activeTab} />

        {renderTabContent()}
      </section>

      <ConfirmReauthorization open={open} handleClose={handleClose} handleConfirm={handleLogout} />

      <Toast
        open={!!user?.provider_changed}
        handleClose={handleCloseToast}
        severity="info"
        message={t('provider.authMethodChanged')}
        autoHideDuration={null}
      />
    </>
  );
}

export default UserProfile;
